/**
 * Shared modal-layer contract for theme-owned drawers and flyouts.
 *
 * Custom drawers use the manager for stack-aware Escape handling, focus
 * containment, exact focus return, and owner-aware body scroll locking.
 * Native dialogs keep the browser focus contract and use only the shared
 * layer/scroll ownership so Escape cannot leak to an underlying drawer.
 */
(function () {
  'use strict';

  if (window.DrhOverlayManager) return;

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary:first-of-type',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var layers = [];
  var scrollOwners = new Set();
  var legacyScrollOwners = [];
  var savedScrollY = 0;
  var savedBodyTop = '';
  var bodyWasLocked = false;
  var focusEpoch = 0;
  var redirectingFocus = false;

  function isElement(value) {
    return value instanceof window.HTMLElement || value instanceof window.SVGElement;
  }

  function isUnavailable(element, boundary) {
    if (!isElement(element) || !element.isConnected) return true;
    if (element.matches(':disabled, [aria-disabled="true"]')) return true;

    var node = element;
    while (node && node !== boundary.parentElement) {
      if (node.hidden || node.hasAttribute('inert') || node.getAttribute('aria-hidden') === 'true') {
        return true;
      }
      if (window.getComputedStyle) {
        var style = window.getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return true;
      }
      if (node.tagName === 'DETAILS' && !node.open) {
        var summary = Array.from(node.children).find(function (child) { return child.tagName === 'SUMMARY'; });
        if (!summary || !summary.contains(element)) return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  function getFocusable(container) {
    if (!isElement(container) || !container.isConnected) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (element) {
      return element.tabIndex >= 0 && !isUnavailable(element, container);
    }).sort(function (a, b) {
      var aOrder = a.tabIndex > 0 ? a.tabIndex : Infinity;
      var bOrder = b.tabIndex > 0 ? b.tabIndex : Infinity;
      return aOrder === bOrder ? (a.compareDocumentPosition(b) & 2 ? 1 : -1) : aOrder - bOrder;
    });
  }

  function resolveTarget(target, layer) {
    var resolved = typeof target === 'function' ? target() : target;
    if (typeof resolved === 'string' && layer && layer.panel) {
      resolved = layer.panel.querySelector(resolved);
    }
    return isElement(resolved) ? resolved : null;
  }

  function safeFocus(target, options) {
    var element = resolveTarget(target);
    if (!element || isUnavailable(element, document.documentElement)) return false;
    try {
      element.focus({ preventScroll: !options || options.preventScroll !== false });
    } catch (_error) {
      element.focus();
    }
    return document.activeElement === element;
  }

  function nativeModalOwnsFocus(layer) {
    var modal;
    try { modal = document.querySelector('dialog:modal'); } catch (_error) { return false; }
    return modal && (!layer || !layer.native || layer.root !== modal);
  }

  function focusLayer(layer, preferred) {
    if (!layer || layer.native || !layer.panel || !layer.panel.isConnected || nativeModalOwnsFocus(layer)) return false;
    var target = resolveTarget(preferred, layer);
    if (target && !isUnavailable(target, layer.panel) && safeFocus(target)) return true;

    var focusable = getFocusable(layer.panel);
    if (focusable.length) return safeFocus(focusable[0]);

    if (!layer.panel.hasAttribute('tabindex')) {
      layer.panel.setAttribute('tabindex', '-1');
      layer.addedPanelTabIndex = true;
    }
    return safeFocus(layer.panel);
  }

  function topLayer() {
    while (layers.length && (!layers[layers.length - 1].root || !layers[layers.length - 1].root.isConnected)) {
      layers[layers.length - 1].destroy();
    }
    return layers[layers.length - 1] || null;
  }

  function updateActiveMarkers() {
    layers.forEach(function (layer, index) {
      if (!layer.root || !layer.root.isConnected) return;
      layer.root.setAttribute('data-drh-overlay-active', index === layers.length - 1 ? 'true' : 'false');
    });
  }

  function lockScroll(owner) {
    var token = owner;
    if (!token) {
      token = {};
      legacyScrollOwners.push(token);
    }
    if (scrollOwners.has(token)) return token;

    if (scrollOwners.size === 0) {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      savedBodyTop = document.body.style.top;
      bodyWasLocked = document.body.classList.contains('no-scroll');
      if (!bodyWasLocked) {
        document.body.style.top = '-' + savedScrollY + 'px';
        document.body.classList.add('no-scroll');
      }
    }
    scrollOwners.add(token);
    return token;
  }

  function unlockScroll(owner) {
    var token = owner;
    if (!token) token = legacyScrollOwners.pop();
    if (!token || !scrollOwners.delete(token) || scrollOwners.size > 0) return;

    if (!bodyWasLocked) {
      document.body.classList.remove('no-scroll');
      document.body.style.top = savedBodyTop;
      window.scrollTo(0, savedScrollY);
    }
    savedScrollY = 0;
    savedBodyTop = '';
    bodyWasLocked = false;
  }

  window.__scrollLock = {
    lock: lockScroll,
    unlock: unlockScroll,
    isLocked: function (owner) { return owner ? scrollOwners.has(owner) : scrollOwners.size > 0; },
    count: function () { return scrollOwners.size; }
  };

  function removeLayer(layer) {
    var index = layers.indexOf(layer);
    if (index !== -1) layers.splice(index, 1);
    if (layer.root && layer.root.isConnected) layer.root.removeAttribute('data-drh-overlay-active');
    updateActiveMarkers();
  }

  function create(config) {
    var options = config || {};
    var layer = {
      root: options.root || null,
      panel: options.panel || options.root || null,
      modal: options.modal !== false,
      native: options.native === true,
      lockScroll: options.lockScroll !== false,
      onEscape: typeof options.onEscape === 'function' ? options.onEscape : null,
      trigger: null,
      fallbackFocus: options.fallbackFocus || null,
      initialFocus: options.initialFocus || null,
      opened: false,
      addedPanelTabIndex: false,

      open: function (openOptions) {
        var next = openOptions || {};
        if (next.root) this.root = next.root;
        if (next.panel) this.panel = next.panel;
        if (Object.prototype.hasOwnProperty.call(next, 'trigger')) this.trigger = resolveTarget(next.trigger);
        if (Object.prototype.hasOwnProperty.call(next, 'fallbackFocus')) this.fallbackFocus = next.fallbackFocus;
        if (Object.prototype.hasOwnProperty.call(next, 'initialFocus')) this.initialFocus = next.initialFocus;
        if (!this.root || !this.panel) return this;

        removeLayer(this);
        // Native dialogs live in the browser top layer and can never be
        // visually covered by a regular positioned drawer. Keep every custom
        // layer below the first open native layer so the keyboard/focus stack
        // cannot disagree with what the user sees.
        var firstNativeIndex = layers.findIndex(function (openLayer) { return openLayer.native; });
        if (!this.native && firstNativeIndex !== -1) {
          layers.splice(firstNativeIndex, 0, this);
        } else {
          layers.push(this);
        }
        this.opened = true;
        if (this.lockScroll) lockScroll(this);
        updateActiveMarkers();

        if (!this.native) {
          var activeLayer = this;
          var activeAtOpen = document.activeElement;
          var openingEpoch = focusEpoch;
          window.requestAnimationFrame(function () {
            if (activeLayer.opened && topLayer() === activeLayer && openingEpoch === focusEpoch && document.activeElement === activeAtOpen) {
              focusLayer(activeLayer, activeLayer.initialFocus);
            }
          });
        }
        return this;
      },

      update: function (updateOptions) {
        var next = updateOptions || {};
        var previousRoot = this.root;
        if (next.root) this.root = next.root;
        if (next.panel) this.panel = next.panel;
        if (Object.prototype.hasOwnProperty.call(next, 'initialFocus')) this.initialFocus = next.initialFocus;
        if (Object.prototype.hasOwnProperty.call(next, 'fallbackFocus')) this.fallbackFocus = next.fallbackFocus;
        if (Object.prototype.hasOwnProperty.call(next, 'onEscape')) this.onEscape = next.onEscape;
        if (previousRoot && previousRoot !== this.root && previousRoot.isConnected) {
          previousRoot.removeAttribute('data-drh-overlay-active');
        }
        updateActiveMarkers();
        if (next.focus === true && topLayer() === this) focusLayer(this, this.initialFocus);
        return this;
      },

      close: function (closeOptions) {
        if (!this.opened) return this;
        var next = closeOptions || {};
        // Use the raw stack here: topLayer() prunes disconnected roots by
        // destroying them, and destroy() delegates back to close(). Calling
        // topLayer() from this path would therefore recurse for a detached
        // active root.
        var wasTop = layers[layers.length - 1] === this;
        this.opened = false;
        removeLayer(this);
        if (this.lockScroll) unlockScroll(this);
        if (this.addedPanelTabIndex && this.panel) {
          this.panel.removeAttribute('tabindex');
          this.addedPanelTabIndex = false;
        }

        if (next.returnFocus !== false && wasTop) {
          var triggerTarget = resolveTarget(this.trigger);
          var fallbackTarget = resolveTarget(next.fallbackFocus || this.fallbackFocus);
          var remainingTop = topLayer();
          if (remainingTop && (!triggerTarget || !remainingTop.panel.contains(triggerTarget))) {
            focusLayer(remainingTop, remainingTop.initialFocus);
          } else if (!safeFocus(triggerTarget) && !safeFocus(fallbackTarget) && remainingTop) {
            focusLayer(remainingTop, remainingTop.initialFocus);
          }
        }
        this.trigger = null;
        return this;
      },

      destroy: function () {
        this.close({ returnFocus: false });
        removeLayer(this);
        this.root = null;
        this.panel = null;
        return this;
      },

      isOpen: function () { return this.opened; },
      isTop: function () { return this.opened && topLayer() === this; }
    };

    return layer;
  }

  function trapTab(event, layer) {
    var focusable = getFocusable(layer.panel);
    if (!focusable.length) {
      event.preventDefault();
      focusLayer(layer);
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;
    if (!layer.panel.contains(active)) {
      event.preventDefault();
      safeFocus(event.shiftKey ? last : first);
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      safeFocus(last);
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      safeFocus(first);
    }
  }

  document.addEventListener('pointerdown', function () { focusEpoch += 1; }, true);

  document.addEventListener('keydown', function (event) {
    focusEpoch += 1;
    var layer = topLayer();
    if (!layer || nativeModalOwnsFocus(layer)) return;

    if (event.key === 'Escape') {
      event.stopImmediatePropagation();
      if (!layer.native && typeof layer.onEscape === 'function') {
        event.preventDefault();
        layer.onEscape(event);
      }
      return;
    }

    if (event.key === 'Tab' && layer.modal && !layer.native) {
      trapTab(event, layer);
      if (event.defaultPrevented) return;
      var focusable = getFocusable(layer.panel);
      var index = focusable.indexOf(document.activeElement);
      var target = focusable[(index + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length];
      var tabEpoch = focusEpoch;
      // Some keyboard profiles skip controls in the browser's Tab order and
      // drop focus onto BODY without firing focusin. Recover only that loss
      // after native navigation, never override a real subsequent focus move.
      window.setTimeout(function () {
        if (tabEpoch !== focusEpoch || topLayer() !== layer || event.defaultPrevented || nativeModalOwnsFocus(layer)) return;
        if (document.activeElement === document.body || document.activeElement === document.documentElement) {
          if (!safeFocus(target)) focusLayer(layer);
        }
      }, 0);
    }
  }, true);

  document.addEventListener('focusin', function (event) {
    focusEpoch += 1;
    var layer = topLayer();
    if (!layer || !layer.modal || layer.native || layer.panel.contains(event.target) || redirectingFocus) return;
    redirectingFocus = true;
    try { focusLayer(layer, layer.initialFocus); }
    finally { redirectingFocus = false; }
  }, true);

  document.addEventListener('shopify:section:unload', function (event) {
    layers.slice().forEach(function (layer) {
      if (event.target && layer.root && event.target.contains(layer.root)) layer.destroy();
    });
  });

  window.DrhOverlayManager = {
    create: create,
    getFocusable: getFocusable,
    safeFocus: safeFocus,
    activeLayer: topLayer
  };
})();
